(function () {
  'use strict';
  if (window.HSProductSelection) return;
  const instances = new WeakMap();
  const live = new Set();
  const selector = '[data-hs-selection-mode][data-hs-product-id]';
  const selectionParams = ['variant', 'option_values', 'hs_color', 'hs_size', 'hs_group', 'hs_explicit_size', 'section_id'];
  const sampleColorKey = value => typeof value === 'string' ? value.trim().toLowerCase() : '';
  const sampleId = value => typeof value === 'string' ? /^[1-9]\d*$/.test(value) : Number.isSafeInteger(value) && value > 0;
  const buildSampleIndex = (data, owner, pageUrl) => {
    if (!data || data.schema !== 1 || String(data.ownerId) !== owner.id || String(data.groupId) !== owner.group ||
      data.ownerType === 'Próbki tkanin' || data.sampleType !== 'Próbki tkanin' || !sampleId(data.sampleId) ||
      String(data.sampleId) === owner.id || !Number.isInteger(data.count) || data.count < 1 || data.count > 250 ||
      !Array.isArray(data.ids) || !Array.isArray(data.colors) || !Array.isArray(data.available) ||
      [data.ids, data.colors, data.available].some(values => values.length !== data.count)) return null;
    const page = new URL(pageUrl);
    const base = new URL(data.url, page.origin);
    if (base.origin !== page.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(base.pathname)) return null;
    base.search = ''; base.hash = '';
    const preview = page.searchParams.get('preview_theme_id');
    if (/^[1-9]\d*$/.test(preview || '')) base.searchParams.set('preview_theme_id', preview);
    const index = new Map();
    const ids = new Set();
    for (let i = 0; i < data.count; i++) {
      const key = sampleColorKey(data.colors[i]);
      const id = String(data.ids[i]);
      if (!key || index.has(key) || !sampleId(data.ids[i]) || ids.has(id) || typeof data.available[i] !== 'boolean') return null;
      const url = new URL(base.href);
      url.searchParams.set('variant', id);
      index.set(key, { label: data.colors[i], available: data.available[i], href: url.pathname + url.search, id });
      ids.add(id);
    }
    return index;
  };
  const readVariant = (scope, strict) => {
    const data = scope.querySelector('[data-current-variant-json]');
    if (strict && !data) throw new Error('Missing variant data');
    try { return JSON.parse(data?.textContent || 'null'); }
    catch (_) { if (strict) throw new Error('Invalid variant data'); return null; }
  };
  class Selection {
    constructor(native) {
      this.native = native;
      this.root = native.container;
      this.id = this.root.dataset.hsProductId;
      this.section = this.root.dataset.sectionId;
      this.group = this.root.dataset.hsSiblingGroup || '';
      this.options = Array.from(this.root.querySelectorAll('[data-explicit-variant-select]')).map(select => ({
        kind: select.dataset.explicitVariantSelect,
        position: Number(select.dataset.index.replace('option', '')) - 1,
        values: Array.from(select.options).filter(o => o.dataset.optionValueId).map(o => ({ id: o.dataset.optionValueId, value: o.value }))
      }));
      if (!this.options.length || this.options.some(o => !o.values.length)) throw new Error('Invalid explicit selection options');
      this.colorResetEnabled = this.root.dataset.isFullProduct === 'true' && Boolean(this.group) &&
        ['SIBLING_COLOR', 'SIBLING_COLOR_SIZE'].includes(this.root.dataset.hsSelectionMode) &&
        this.options.some(o => o.kind === 'color');
      this.labels = JSON.parse(this.root.querySelector('[data-hs-selection-labels]').textContent);
      this.sequence = 0;
      this.state = {};
      this.confirmed = null;
      this.preview = null;
      this.baseline = null;
      this.destroyed = false;
      this.cleanups = [];
      this.debug = { requests: 0, commits: 0, ignored: 0, errors: [], selections: [] };
      const url = new URL(location.href);
      if (!url.searchParams.has('variant') && !url.searchParams.has('option_values')) this.baseline = this.root.cloneNode(true);
      this.ui = document.createElement('div');
      this.ui.className = 'hs-selection-actions';
      this.clearButton = null;
      if (this.colorResetEnabled) {
        this.clearButton = document.createElement('button');
        this.clearButton.type = 'button';
        this.clearButton.dataset.hsSelectionClear = '';
        this.clearButton.textContent = this.labels.clear;
        this.clearButton.hidden = true;
        this.ui.append(this.clearButton);
      }
      this.retryButton = document.createElement('button');
      this.retryButton.type = 'button';
      this.retryButton.dataset.hsSelectionRetry = '';
      this.retryButton.textContent = this.labels.retry;
      this.retryButton.hidden = true;
      this.message = document.createElement('span');
      this.message.dataset.hsSelectionStatus = '';
      this.message.setAttribute('role', 'status');
      this.message.setAttribute('aria-live', 'polite');
      this.ui.append(this.message, this.retryButton);
      (this.root.querySelector('.product__variants-wrapper') || this.root.querySelector('.product__meta-content')).insertAdjacentElement('afterend', this.ui);
      this.listen(this.root, 'click', e => this.click(e), true);
      this.listen(this.root, 'change', e => this.change(e), true);
      this.listen(this.root, 'submit', e => {
        const actual = e.target.querySelector('[data-variant-id-input]')?.value;
        if (!this.canBuy() || String(this.confirmed.id) !== actual) {
          e.preventDefault(); e.stopImmediatePropagation(); this.apply();
        }
      }, true);
      if (this.clearButton) this.listen(this.clearButton, 'click', () => this.clear());
      this.listen(this.retryButton, 'click', () => this.request(this.urlVariant));
      this.listen(window, 'popstate', () => this.fromURL());
      this.listen(window, 'pageshow', e => { if (e.persisted) this.fromURL(); });
      this.root.dataset.hsSelectionReady = 'true';
      this.fromURL(true);
    }
    listen(target, name, handler, capture) {
      target.addEventListener(name, handler, capture);
      this.cleanups.push(() => target.removeEventListener(name, handler, capture));
    }
    placeClearButton() {
      if (!this.clearButton) return;
      const colorButton = this.root.querySelector('[data-explicit-variant-button="color"]');
      const swatches = colorButton?.closest('.product__color-swatches--inner');
      const target = swatches || this.ui;
      if (this.clearButton.parentElement !== target) {
        if (swatches) swatches.appendChild(this.clearButton);
        else this.ui.prepend(this.clearButton);
      }
      this.clearButton.classList.toggle('hs-selection-clear--inline', Boolean(swatches));
    }
    syncFabricSampleLink() {
      if (this.root.dataset.isFullProduct !== 'true' || !['SIBLING_COLOR', 'SIBLING_COLOR_SIZE'].includes(this.root.dataset.hsSelectionMode)) return;
      const blocks = this.root.querySelectorAll('[data-hs-fabric-sample]');
      const hide = block => {
        block.hidden = true;
        const link = block.querySelector('[data-hs-fabric-sample-link]');
        if (link) {
          link.removeAttribute('href');
          link.removeAttribute('aria-label');
          const colorLabel = link.querySelector('[data-hs-sample-color]');
          if (colorLabel) colorLabel.textContent = '';
          else link.textContent = '';
        }
      };
      if (blocks.length !== 1) { blocks.forEach(hide); this.sampleLinkCache = null; return; }
      const block = blocks[0];
      try {
        const raw = block.querySelector('[data-hs-fabric-sample-data]')?.textContent;
        const link = block.querySelector('[data-hs-fabric-sample-link]');
        if (!link) { hide(block); return; }
        const preview = new URL(location.href).searchParams.get('preview_theme_id') || '';
        if (!this.sampleLinkCache || this.sampleLinkCache.raw !== raw || this.sampleLinkCache.preview !== preview) {
          this.sampleLinkCache = { raw, preview, index: null };
          this.sampleLinkCache.index = buildSampleIndex(JSON.parse(raw), this, location.href);
        }
        const color = this.state.color;
        const knownColor = color && this.options.find(o => o.kind === 'color')?.values.some(v => v.id === color.id && v.value === color.value);
        const entry = knownColor && this.sampleLinkCache.index?.get(sampleColorKey(color.value));
        const label = link.dataset.hsSampleLabel || '';
        if (!entry || !entry.available || !label.includes('__HS_SAMPLE_COLOR__')) { hide(block); return; }
        link.setAttribute('href', entry.href);
        const accessibleLabel = label.replace('__HS_SAMPLE_COLOR__', entry.label);
        const colorLabel = link.querySelector('[data-hs-sample-color]');
        link.setAttribute('aria-label', accessibleLabel);
        if (colorLabel) colorLabel.textContent = entry.label;
        else link.textContent = accessibleLabel;
        block.hidden = false;
      } catch (_) { hide(block); }
    }
    isComplete() { return this.options.every(o => Boolean(this.state[o.kind]?.id)); }
    validVariant(variant) {
      return Boolean(variant && Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0 &&
        Array.isArray(variant.options) && variant.options.length === this.options.length &&
        typeof variant.available === 'boolean' && Number.isFinite(variant.price) && variant.price >= 0 &&
        typeof variant.sku === 'string' && this.options.every(o => o.values.some(v => v.value === variant.options[o.position])));
    }
    matches(variant) {
      return this.validVariant(variant) && this.options.every(o => !this.state[o.kind] || variant.options[o.position] === this.state[o.kind].value);
    }
    canBuy() { return this.isComplete() && this.matches(this.confirmed) && this.confirmed.available === true && !this.pending; }
    emit(phase) {
      this.root.dispatchEvent(new CustomEvent('hs:selection-change', { bubbles: true, detail: {
        productId: this.id, sectionId: this.section, requestId: this.sequence, phase,
        color: this.state.color?.value || '', size: this.state.size?.value || '',
        previewVariant: this.preview, variant: this.confirmed, purchaseReady: this.canBuy(), root: this.root
      } }));
    }
    label() {
      if (this.pending) return this.labels.loading;
      const color = this.options.some(o => o.kind === 'color') && !this.state.color;
      const size = this.options.some(o => o.kind === 'size') && !this.state.size;
      if (color && size) return this.root.dataset.explicitLabelBoth;
      if (color) return this.root.dataset.explicitLabelColor;
      if (size) return this.root.dataset.explicitLabelSize;
      if (this.error) return this.labels.retry;
      return this.labels.unavailable;
    }
    apply() {
      const ready = this.isComplete() && this.matches(this.confirmed) && !this.pending;
      const buy = this.canBuy();
      this.root.inert = false;
      this.root.removeAttribute('data-variant-dimmed');
      this.root.dataset.variantSelectionState = ready ? (buy ? 'complete' : 'unavailable') : 'incomplete';
      this.root.dataset.colorSelectionState = this.state.color ? 'selected' : 'empty';
      this.root.dataset.variantLoading = this.pending ? 'true' : 'false';
      this.root.dataset.hsSelectionRequest = String(this.sequence);
      this.options.forEach(o => {
        const selected = this.state[o.kind];
        const prefix = o.kind === 'color' ? 'explicitColor' : 'explicitSize';
        this.root.dataset[prefix + 'ValueId'] = selected?.id || '';
        this.root.dataset[prefix + 'Value'] = selected?.value || '';
        const select = this.root.querySelector('[data-explicit-variant-select="' + o.kind + '"]');
        if (select) select.value = selected?.value || '';
        this.root.querySelectorAll('[data-explicit-variant-button="' + o.kind + '"]').forEach(button => {
          const yes = Boolean(selected && button.dataset.optionValueId === selected.id);
          button.classList.toggle('selected', yes);
          button.setAttribute('aria-pressed', String(yes));
        });
        const label = select?.closest('.product__option')?.querySelector('[data-selected-value-for-option]');
        if (label) label.textContent = selected?.value || this.root.dataset[o.kind === 'color' ? 'explicitLabelColor' : 'explicitLabelSize'];
      });
      this.root.querySelectorAll('[data-variant-id-input]').forEach(input => { input.value = ready ? String(this.confirmed.id) : ''; });
      const variantJSON = this.root.querySelector('[data-current-variant-json]');
      if (variantJSON) variantJSON.textContent = JSON.stringify(ready ? this.confirmed : null);
      this.root.querySelectorAll('[data-add-to-cart]').forEach(button => {
        button.disabled = !buy;
        button.setAttribute('aria-disabled', String(!buy));
        const label = buy ? button.dataset.langAvailable : this.label();
        button.setAttribute('aria-label', label);
        const text = button.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = this.pending ? button.dataset.langAvailable : label;
      });
      this.root.querySelectorAll('[data-explicit-selection-gated]').forEach(el => { el.hidden = !buy; });
      this.root.querySelectorAll('.product__vendor_and_sku').forEach(el => { el.hidden = !ready; });
      if (!ready) {
        this.root.querySelectorAll('[data-price]').forEach(el => { el.textContent = this.root.dataset.explicitFromPrice; });
        this.root.querySelectorAll('[data-compare-price]').forEach(el => { el.textContent = ''; });
        this.root.querySelectorAll('.product__price-displayed-discount').forEach(el => { el.hidden = true; });
        this.root.querySelectorAll('[data-store-availability-container]').forEach(el => { el.innerHTML = ''; });
      }
      this.placeClearButton();
      this.syncFabricSampleLink();
      if (this.clearButton) this.clearButton.hidden = !this.state.color;
      this.retryButton.hidden = !this.error;
      this.message.textContent = this.error ? this.labels.error : (this.pending ? this.labels.loading : '');
      this.message.classList.toggle('visually-hidden', Boolean(this.pending && !this.error));
      this.root.querySelectorAll('[data-sibling-swatch]').forEach(link => {
        if (!link.dataset.hsOriginalHref) link.dataset.hsOriginalHref = link.getAttribute('href');
        const target = new URL(link.dataset.hsOriginalHref, location.origin);
        selectionParams.forEach(k => target.searchParams.delete(k));
        if (link.getAttribute('aria-current') === 'page') {
          if (ready) target.searchParams.set('variant', String(this.confirmed.id));
          else {
            if (this.state.color) target.searchParams.set('hs_color', this.state.color.value);
            if (this.state.size) target.searchParams.set('hs_size', this.state.size.value);
          }
        } else if (this.state.size && this.group) {
          target.searchParams.set('hs_size', this.state.size.value);
          target.searchParams.set('hs_group', this.group);
        }
        link.setAttribute('href', target.pathname + target.search + target.hash);
      });
    }
    click(e) {
      const currentFamily = e.target.closest('[data-sibling-swatch][aria-current="page"]');
      if (currentFamily && currentFamily.closest(selector) === this.root && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
        e.preventDefault(); e.stopImmediatePropagation(); return;
      }
      const button = e.target.closest('[data-explicit-variant-button]');
      if (!button || button.closest(selector) !== this.root) return;
      e.preventDefault(); e.stopImmediatePropagation();
      if (button.disabled || button.classList.contains('disabled')) return;
      this.choose(button.dataset.explicitVariantButton, button.dataset.optionValueId, true);
    }
    change(e) {
      const select = e.target.closest('[data-explicit-variant-select]');
      if (!select || select.closest(selector) !== this.root) return;
      e.preventDefault(); e.stopImmediatePropagation();
      this.choose(select.dataset.explicitVariantSelect, select.selectedOptions[0]?.dataset.optionValueId, true);
    }
    choose(kind, id, fromControl) {
      const value = this.options.find(o => o.kind === kind)?.values.find(v => v.id === id);
      if (!value || this.state[kind]?.id === id) return;
      this.state[kind] = value;
      this.urlVariant = null;
      this.confirmed = null;
      this.error = false;
      this.debug.selections.push({ kind, value: value.value, at: performance.now() });
      if (this.debug.selections.length > 100) this.debug.selections.shift();
      this.writeURL(true);
      this.request();
      if (kind === 'color' && fromControl) this.scrollToGallery();
    }
    stopGalleryScroll() {
      this.galleryScroll?.finish();
    }
    scrollToGallery() {
      const mode = this.root.dataset.hsSelectionMode;
      if (!['SIBLING_COLOR', 'SIBLING_COLOR_SIZE', 'FABRIC_SAMPLE'].includes(mode) || this.destroyed) return;
      this.stopGalleryScroll();
      const gallery = this.root.querySelector('.product__primary-left');
      if (!gallery || !gallery.getClientRects().length) return;
      const start = window.scrollY;
      const left = window.scrollX;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--height-header')) || 0;
      const target = Math.max(0, Math.min(document.documentElement.scrollHeight - window.innerHeight, gallery.getBoundingClientRect().top + start - header - 12));
      if (start <= target + 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.scrollTo({ top: target, left, behavior: 'instant' });
        return;
      }
      const duration = Math.min(360, Math.max(240, (start - target) / 6));
      const started = performance.now();
      let frame;
      let timeout;
      let done = false;
      let touching = false;
      let arrived = false;
      const touchTargets = new Set();
      const releaseTouchTargets = () => {
        touchTargets.forEach(node => {
          node.removeEventListener('touchstart', touchStart, true);
          node.removeEventListener('touchmove', touchMove, true);
          node.removeEventListener('touchend', touchEnd, true);
          node.removeEventListener('touchcancel', touchEnd, true);
        });
        touchTargets.clear();
      };
      const finish = () => {
        if (done) return;
        done = true;
        cancelAnimationFrame(frame);
        clearTimeout(timeout);
        releaseTouchTargets();
        document.removeEventListener('touchstart', touchStart, true);
        document.removeEventListener('touchmove', touchMove, true);
        document.removeEventListener('touchend', touchEnd, true);
        document.removeEventListener('touchcancel', touchEnd, true);
        document.removeEventListener('wheel', wheel, true);
        document.removeEventListener('keydown', keyboard, true);
        document.removeEventListener('visibilitychange', visibility);
        window.removeEventListener('pagehide', finish);
        if (this.galleryScroll?.finish === finish) {
          this.galleryScroll = null;
          delete this.root.dataset.hsGalleryScrolling;
        }
      };
      const touchStart = event => {
        if (done) return;
        if (event.touches.length !== 1) { finish(); return; }
        touching = true;
        const node = event.target;
        if (node && node !== document && !touchTargets.has(node)) {
          touchTargets.add(node);
          node.addEventListener('touchstart', touchStart, { capture: true, passive: true });
          node.addEventListener('touchmove', touchMove, { capture: true, passive: false });
          node.addEventListener('touchend', touchEnd, { capture: true, passive: true });
          node.addEventListener('touchcancel', touchEnd, { capture: true, passive: true });
        }
      };
      const touchEnd = event => {
        if (done) return;
        if (event.touches.length) return;
        touching = false;
        releaseTouchTargets();
        if (arrived) finish();
      };
      const touchMove = event => {
        if (done) return;
        if (event.touches.length !== 1) { finish(); return; }
        if (event.cancelable) event.preventDefault();
      };
      const wheel = event => {
        if (event.ctrlKey) { finish(); return; }
        if (event.cancelable) event.preventDefault();
      };
      const keyboard = event => { if (['Escape', 'Tab', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) finish(); };
      const visibility = () => { if (document.hidden) finish(); };
      const tick = now => {
        if (done) return;
        if (this.destroyed || !this.root.isConnected) { finish(); return; }
        const progress = Math.min(1, (now - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        window.scrollTo({ top: start + (target - start) * eased, left, behavior: 'instant' });
        if (progress < 1) frame = requestAnimationFrame(tick);
        else { arrived = true; if (!touching) finish(); }
      };
      this.galleryScroll = { finish };
      this.root.dataset.hsGalleryScrolling = 'true';
      document.addEventListener('touchstart', touchStart, { capture: true, passive: true });
      document.addEventListener('touchmove', touchMove, { capture: true, passive: false });
      document.addEventListener('touchend', touchEnd, { capture: true, passive: true });
      document.addEventListener('touchcancel', touchEnd, { capture: true, passive: true });
      document.addEventListener('wheel', wheel, { capture: true, passive: false });
      document.addEventListener('keydown', keyboard, true);
      document.addEventListener('visibilitychange', visibility);
      window.addEventListener('pagehide', finish);
      timeout = setTimeout(finish, 750);
      frame = requestAnimationFrame(tick);
    }
    writeURL(push, variant) {
      const url = new URL(location.href);
      selectionParams.forEach(k => url.searchParams.delete(k));
      if (variant) url.searchParams.set('variant', String(variant.id));
      else {
        if (this.state.color) url.searchParams.set('hs_color', this.state.color.value);
        if (this.state.size) url.searchParams.set('hs_size', this.state.size.value);
      }
      const target = url.pathname + url.search + url.hash;
      if (target !== location.pathname + location.search + location.hash) history[push ? 'pushState' : 'replaceState']({ hsSelection: true }, '', target);
    }
    cancel() {
      this.sequence++;
      this.abort?.abort();
      this.native.cancelHSAvailability?.();
      clearTimeout(this.timer);
      this.pending = false;
    }
    clear() {
      if (!this.colorResetEnabled || !this.state.color) return;
      this.stopGalleryScroll();
      this.cancel();
      delete this.state.color;
      this.confirmed = this.preview = this.urlVariant = null;
      this.error = false;
      this.writeURL(true);
      this.apply();
      this.emit('reset');
      if (this.baseline) {
        this.native._swapMediaFromDoc(this.baseline);
        this.emit('baseline');
      } else this.request();
    }
    fromURL(initial) {
      this.stopGalleryScroll();
      this.cancel();
      const url = new URL(location.href);
      const initialVariant = initial ? readVariant(this.root) : null;
      this.state = {};
      this.confirmed = this.preview = null;
      this.error = false;
      this.urlVariant = /^\d+$/.test(url.searchParams.get('variant') || '') ? url.searchParams.get('variant') : null;
      // Adopt server-rendered sample data without a request or automatic scroll.
      // Explicit links, reset and browser history never fall back to a first variant.
      const defaultSample = initial && this.root.dataset.hsSelectionMode === 'FABRIC_SAMPLE' &&
        this.options.length === 1 && this.options[0].kind === 'color' &&
        !selectionParams.some(key => key !== 'section_id' && url.searchParams.has(key)) &&
        initialVariant && String(initialVariant.id) === this.root.dataset.hsDefaultSampleVariant;
      const exactLink = this.urlVariant && initialVariant && String(initialVariant.id) === this.urlVariant;
      if ((exactLink || defaultSample) && this.adoptVariant(initialVariant)) {
        this.confirmed = this.preview = initialVariant;
        if (defaultSample) this.writeURL(false, initialVariant);
        this.apply();
        const initialSequence = this.sequence;
        this.native.refreshHSAvailability(initialVariant, () => !this.destroyed && this.sequence === initialSequence);
        this.emit('resolved'); return;
      }
      if (!this.urlVariant) {
        const ids = (url.searchParams.get('option_values') || '').split(',');
        this.options.forEach(o => {
          const direct = o.values.find(v => ids.includes(v.id));
          const hint = url.searchParams.get('hs_' + o.kind);
          const groupOK = o.kind !== 'size' || !url.searchParams.has('hs_group') || url.searchParams.get('hs_group') === this.group;
          const match = direct || (groupOK ? o.values.find(v => v.value === hint) : null);
          if (match) this.state[o.kind] = match;
        });
      }
      this.apply();
      if (this.urlVariant || Object.keys(this.state).length) this.request(this.urlVariant);
      else {
        this.emit('reset');
        if (!initial) {
          if (this.baseline) { this.native._swapMediaFromDoc(this.baseline); this.emit('baseline'); }
          else this.request();
        }
      }
    }
    adoptVariant(variant) {
      if (!this.validVariant(variant)) return false;
      const state = {};
      for (const option of this.options) {
        const value = option.values.find(v => v.value === variant.options?.[option.position]);
        if (!value) return false;
        state[option.kind] = value;
      }
      this.state = state; return true;
    }
    async request(variantId) {
      this.cancel();
      const baselineOnly = this.colorResetEnabled && !variantId && !this.state.color;
      const seq = this.sequence;
      this.pending = true;
      this.error = false;
      this.confirmed = null;
      this.preview = null;
      this.abort = new AbortController();
      const abort = this.abort;
      const isCurrent = () => !this.destroyed && this.root.isConnected && this.sequence === seq && !abort.signal.aborted;
      const url = new URL(this.root.dataset.hsProductUrl, location.origin);
      url.searchParams.set('section_id', this.section);
      const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      if (variantId) url.searchParams.set('variant', variantId);
      else if (!baselineOnly) {
        const ids = this.options.map(o => this.state[o.kind]?.id).filter(Boolean);
        if (ids.length) url.searchParams.set('option_values', ids.join(','));
      }
      this.apply(); this.emit('intent'); this.debug.requests++;
      let timedOut = false;
      this.timer = setTimeout(() => { timedOut = true; abort.abort(); }, 10000);
      try {
        const response = await fetch(url, { signal: abort.signal });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const text = await response.text();
        if (!isCurrent()) { this.debug.ignored++; return; }
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const nextRoot = doc.querySelector(selector);
        if (!nextRoot || nextRoot.dataset.hsProductId !== this.id || nextRoot.dataset.sectionId !== this.section || nextRoot.dataset.hsSelectionMode !== this.root.dataset.hsSelectionMode) throw new Error('Wrong product section');
        let variant = readVariant(nextRoot, true);
        if (baselineOnly && variant !== null) throw new Error('Unexpected variant in base-gallery response');
        if (variant !== null && !this.validVariant(variant)) throw new Error('Invalid variant fields');
        if (variantId) {
          if (!variant || String(variant.id) !== String(variantId) || !this.adoptVariant(variant)) {
            this.state = {}; this.urlVariant = null; variant = null;
          }
        }
        if (variant && !this.matches(variant)) throw new Error('Variant does not match selection');
        if (!isCurrent()) return;
        const mapped = this.root.dataset.hsSelectionMode.startsWith('SIBLING_') && Boolean(window.HSColorGalleries?.owns?.(this.root));
        this.native.commitHSSelection(doc, variant, isCurrent, mapped, this.isComplete());
        if (baselineOnly || !Object.keys(this.state).length) this.baseline = nextRoot.cloneNode(true);
        this.preview = variant;
        this.confirmed = this.isComplete() ? variant : null;
        this.pending = false;
        this.apply();
        this.writeURL(false, this.confirmed);
        this.debug.commits++;
        this.emit(baselineOnly ? 'baseline' : (variant ? 'resolved' : (Object.keys(this.state).length ? 'unavailable' : 'baseline')));
        if (this.confirmed) {
          this.native.formElement.dispatchEvent(new Event('change'));
          document.dispatchEvent(new CustomEvent('product:variant-change', { detail: { variant: this.confirmed, productId: this.id, sectionId: this.section, requestId: seq, root: this.root } }));
        }
      } catch (error) {
        if (seq !== this.sequence || this.destroyed) return;
        if (error.name === 'AbortError' && !timedOut) return;
        this.pending = false; this.error = true; this.confirmed = null;
        this.debug.errors.push({ requestId: seq, reason: timedOut ? 'timeout' : error.message });
        if (this.debug.errors.length > 30) this.debug.errors.shift();
        this.apply(); this.emit('error');
      } finally {
        if (seq === this.sequence) { clearTimeout(this.timer); this.pending = false; this.apply(); }
      }
    }
    destroy() {
      this.stopGalleryScroll();
      this.destroyed = true; this.cancel();
      this.cleanups.forEach(fn => fn()); this.clearButton?.remove(); this.ui.remove(); live.delete(this);
      instances.delete(this.root);
    }
  }
  window.HSProductSelection = {
    bind(native) {
      if (!native.container.matches(selector)) return null;
      if (instances.has(native.container)) return instances.get(native.container);
      const instance = new Selection(native);
      instances.set(native.container, instance); live.add(instance); return instance;
    },
    get(root) { return instances.get(root); },
    version: '1.1.5'
  };
  document.addEventListener('shopify:section:unload', event => {
    live.forEach(instance => { if (event.target.contains(instance.root)) instance.destroy(); });
  });
})();
